<template>
  <v-container v-if="cliente" fluid class="pa-4">

    <v-btn text small color="primary" class="mb-2 ml-n2" @click="volver">
      <v-icon left small>mdi-arrow-left</v-icon>
      Perfil
    </v-btn>

    <h2 class="text-h5 font-weight-bold mb-6">Editar cliente</h2>

    <v-row>
      <v-col cols="12" md="7">
        <v-card>
          <v-card-text>
            <!-- Cambiar de tipo corrige un cliente cargado mal -->
            <div class="caption grey--text mb-2">¿A quién le vendés?</div>
            <v-row dense class="mb-2">
              <v-col cols="6" v-for="op in tipos" :key="op.value">
                <v-card
                  outlined
                  class="tipo-card pa-3 text-center"
                  :class="{ 'tipo-card--activa': form.es_persona_fisica === op.value }"
                  @click="cambiarTipo(op.value)"
                >
                  <v-icon :color="form.es_persona_fisica === op.value ? 'primary' : 'grey'">
                    {{ op.icon }}
                  </v-icon>
                  <div class="body-2 font-weight-medium mt-1">{{ op.text }}</div>
                </v-card>
              </v-col>
            </v-row>

            <v-form ref="form" @submit.prevent="guardar">
              <template v-if="form.es_persona_fisica">
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="form.nombre"
                      label="Nombre *"
                      outlined dense
                      :rules="[reglaRequerido]"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="form.apellido" label="Apellido" outlined dense />
                  </v-col>
                </v-row>
              </template>
              <template v-else>
                <v-text-field
                  v-model="form.razonSocial"
                  label="Razón social *"
                  outlined dense
                  :rules="[reglaRequerido]"
                />
              </template>

              <div class="caption grey--text mt-2 mb-2">Contacto</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.telefono" label="Teléfono" outlined dense hide-details />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="form.email"
                    label="Email"
                    outlined dense
                    hide-details="auto"
                    :rules="[reglaEmail]"
                  />
                </v-col>
              </v-row>

              <div class="caption grey--text mt-4 mb-2">Datos para facturar</div>
              <v-row dense>
                <v-col cols="12" sm="5">
                  <v-select
                    v-model="form.tipo_documento"
                    :items="tiposDocumento"
                    item-text="description"
                    item-value="id"
                    label="Tipo"
                    outlined dense clearable hide-details
                  />
                </v-col>
                <v-col cols="12" sm="7">
                  <v-text-field
                    v-model="form.documento"
                    :label="form.es_persona_fisica ? 'Documento' : 'CUIT'"
                    outlined dense
                    hide-details="auto"
                    :rules="[reglaDocumento]"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="form.condicionIva"
                    :items="condicionesIva"
                    item-text="nombre"
                    item-value="id"
                    label="Condición frente al IVA"
                    outlined dense hide-details
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer />
            <v-btn text @click="volver">Cancelar</v-btn>
            <v-btn color="primary" depressed :loading="guardando" @click="guardar">
              <v-icon left small>mdi-check</v-icon>
              Guardar cambios
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ClienteAPI from "@/api/clientes/clientes";
import { fetchCliente } from "@/mixins/cliente/fetchCliente";

export default {
  name: "cliente-update",
  mixins: [fetchCliente],

  data() {
    return {
      guardando: false,
      tiposDocumento: [],
      condicionesIva: [],
      form: {
        es_persona_fisica: true,
        nombre: "",
        apellido: "",
        razonSocial: "",
        telefono: "",
        email: "",
        documento: "",
        tipo_documento: null,
        condicionIva: null,
      },
      tipos: [
        { value: true, text: "Persona", icon: "mdi-account" },
        { value: false, text: "Empresa", icon: "mdi-domain" },
      ],
    };
  },

  watch: {
    // El mixin trae el cliente de forma asíncrona
    cliente: {
      immediate: true,
      handler(c) {
        if (c) this.cargarForm(c);
      },
    },
  },

  methods: {
    cargarForm(c) {
      const p = c.persona || {};
      this.form = {
        es_persona_fisica: c.es_persona_fisica !== false,
        nombre: c.nombre || p.nombre || "",
        apellido: c.apellido || p.apellido || "",
        razonSocial: c.razonSocial || "",
        telefono: c.telefono || p.telefono || "",
        email: c.email || p.email || "",
        documento: c.documento || p.documento || "",
        tipo_documento: this.idDe(c.tipo_documento) || this.idDe(p.tipo_documento),
        condicionIva: this.idDe(c.condicionIva),
      };
    },

    idDe(valor) {
      if (!valor) return null;
      return typeof valor === "object" ? valor.id : valor;
    },

    // Al pasar de persona a empresa el nombre suelto no sirve como razón
    // social, pero sí es el mejor punto de partida.
    cambiarTipo(esFisica) {
      if (this.form.es_persona_fisica === esFisica) return;
      if (!esFisica && !this.form.razonSocial) {
        this.form.razonSocial = [this.form.nombre, this.form.apellido].filter(Boolean).join(" ");
      }
      this.form.es_persona_fisica = esFisica;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },

    reglaRequerido(v) {
      return !!(v || "").trim() || "Requerido";
    },
    reglaEmail(v) {
      if (!v) return true;
      return /.+@.+\..+/.test(v) || "Email inválido";
    },
    reglaDocumento(v) {
      if (!v) return true;
      return /^\d{7,11}$/.test(v) || "Entre 7 y 11 dígitos, sin puntos ni guiones";
    },

    async cargarOpciones() {
      try {
        const [tipos, condiciones] = await Promise.all([
          this.axios.get("/clientes/tipodocumento"),
          this.axios.get("/clientes/condicioniva"),
        ]);
        this.tiposDocumento = tipos.data;
        this.condicionesIva = condiciones.data;
      } catch (e) {
        console.error(e);
      }
    },

    volver() {
      const esPos = this.$route.path.includes("/pos");
      this.$router.push({
        name: esPos ? "pos.cliente.profile" : "empresa.cliente.profile",
        params: { id_cliente: this.$route.params.id_cliente },
      });
    },

    async guardar() {
      if (!this.$refs.form.validate()) return;
      this.guardando = true;
      try {
        const f = this.form;
        await ClienteAPI.editarBasico(this.$route.params.id_cliente, {
          es_persona_fisica: f.es_persona_fisica,
          nombre: f.es_persona_fisica ? f.nombre.trim() : null,
          apellido: f.es_persona_fisica ? f.apellido.trim() || null : null,
          razonSocial: f.es_persona_fisica ? null : f.razonSocial.trim(),
          telefono: f.telefono.trim() || null,
          email: f.email.trim() || null,
          documento: f.documento.trim() || null,
          tipo_documento: f.tipo_documento,
          condicionIva: f.condicionIva,
        });
        this.$updateBus.$emit("showSnackBar", {
          show: true, button: false, color: "success",
          text: "Cliente actualizado", icon: "mdi-check", timeout: 2500,
        });
        this.volver();
      } catch (e) {
        const data = e.response?.data || {};
        const detalle = data.detail || Object.values(data).flat()[0] || "No se pudo guardar";
        this.$updateBus.$emit("showSnackBar", {
          show: true, button: false, color: "error",
          text: detalle, icon: "mdi-alert", timeout: 4000,
        });
      } finally {
        this.guardando = false;
      }
    },
  },

  created() {
    this.cargarOpciones();
  },
};
</script>

<style scoped>
.tipo-card {
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}
.tipo-card:hover {
  background-color: rgba(255, 255, 255, 0.04);
}
.tipo-card--activa {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(33, 150, 243, 0.08);
}
</style>
